import { useState, useRef, useEffect } from 'react';
import { Search, Filter, ChevronDown, X } from 'lucide-react';

interface FilterState {
  search: string;
  accounts: string[];
  seniority: string;
  department: string;
  outreachStatus: string;
  assignedTo: string;
}

interface Props {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  accountOptions: { id: string; name: string }[];
  teamMembers: { id: string; name: string }[];
  currentUserId: string;
}

const SENIORITY_OPTIONS = [
  { value: '', label: 'All Seniority' },
  { value: 'executive', label: 'Executive' },
  { value: 'executive_leadership', label: 'Executive Leadership' },
  { value: 'senior_leadership', label: 'Senior Leadership' },
  { value: 'department_leader', label: 'Department Leader' },
  { value: 'team_manager', label: 'Team Manager' },
  { value: 'individual_contributor', label: 'Individual Contributor' },
];

const OUTREACH_OPTIONS = [
  { value: '', label: 'All Outreach' },
  { value: 'none', label: 'Never Contacted' },
  { value: 'recent_7', label: 'Last 7 Days' },
  { value: 'recent_30', label: 'Last 30 Days' },
  { value: 'stale', label: 'Over 30 Days' },
];

export default function ContactsFilterBar({ filters, onChange, accountOptions, teamMembers, currentUserId }: Props) {
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setAccountDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const update = (partial: Partial<FilterState>) => onChange({ ...filters, ...partial });

  const toggleAccount = (id: string) => {
    const next = filters.accounts.includes(id)
      ? filters.accounts.filter(a => a !== id)
      : [...filters.accounts, id];
    update({ accounts: next });
  };

  const selectedCount = filters.accounts.length;
  const activeFilterCount = [
    filters.seniority,
    filters.department,
    filters.outreachStatus,
    filters.assignedTo,
  ].filter(Boolean).length + (selectedCount > 0 && selectedCount < accountOptions.length ? 1 : 0);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[240px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-muted" />
        <input
          type="text"
          placeholder="Search contacts by name, role or email..."
          value={filters.search}
          onChange={e => update({ search: e.target.value })}
          className="w-full pl-10 pr-4 py-2.5 bg-app-surface border border-app-line rounded-lg text-sm text-app-ink placeholder:text-app-muted focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-400 transition-colors"
        />
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setAccountDropdownOpen(!accountDropdownOpen)}
          className="flex items-center gap-2 px-3 py-2.5 bg-app-surface border border-app-line rounded-lg text-sm text-app-ink hover:border-app-line-strong transition-colors"
        >
          <Filter className="w-4 h-4 text-app-muted" />
          <span>Accounts</span>
          {selectedCount > 0 && selectedCount < accountOptions.length && (
            <span className="ml-1 px-1.5 py-0.5 bg-forest-100 text-forest-700 text-[10px] font-bold rounded-full">
              {selectedCount}
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-app-muted" />
        </button>

        {accountDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-72 bg-app-surface border border-app-line rounded-lg shadow-none z-50 max-h-80 overflow-y-auto">
            <div className="p-2 border-b border-app-line flex items-center justify-between">
              <button
                onClick={() => update({ accounts: accountOptions.map(a => a.id) })}
                className="text-xs text-forest-600 hover:text-forest-700 font-medium"
              >
                Select All
              </button>
              <button
                onClick={() => update({ accounts: [] })}
                className="text-xs text-app-muted hover:text-app-ink font-medium"
              >
                Clear All
              </button>
            </div>
            <div className="p-1">
              {accountOptions.map(account => (
                <label
                  key={account.id}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-app-canvas cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={filters.accounts.length === 0 || filters.accounts.includes(account.id)}
                    onChange={() => toggleAccount(account.id)}
                    className="w-3.5 h-3.5 rounded border-app-line-strong text-forest-600 focus:ring-forest-500"
                  />
                  <span className="text-sm text-app-ink truncate">{account.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <select
        value={filters.seniority}
        onChange={e => update({ seniority: e.target.value })}
        className="px-3 py-2.5 bg-app-surface border border-app-line rounded-lg text-sm text-app-ink hover:border-app-line-strong focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-400 transition-colors"
      >
        {SENIORITY_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <select
        value={filters.outreachStatus}
        onChange={e => update({ outreachStatus: e.target.value })}
        className="px-3 py-2.5 bg-app-surface border border-app-line rounded-lg text-sm text-app-ink hover:border-app-line-strong focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-400 transition-colors"
      >
        {OUTREACH_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <select
        value={filters.assignedTo}
        onChange={e => update({ assignedTo: e.target.value })}
        className="px-3 py-2.5 bg-app-surface border border-app-line rounded-lg text-sm text-app-ink hover:border-app-line-strong focus:outline-none focus:ring-2 focus:ring-forest-500/20 focus:border-forest-400 transition-colors"
      >
        <option value="">All Assignees</option>
        <option value="me">Assigned to Me</option>
        <option value="unassigned">Unassigned</option>
        {teamMembers.filter(m => m.id !== currentUserId).map(m => (
          <option key={m.id} value={m.id}>{m.name}</option>
        ))}
      </select>

      {activeFilterCount > 0 && (
        <button
          onClick={() => onChange({ search: filters.search, accounts: [], seniority: '', department: '', outreachStatus: '', assignedTo: '' })}
          className="flex items-center gap-1 px-2.5 py-2 text-xs font-medium text-app-muted hover:text-app-ink transition-colors"
        >
          <X className="w-3.5 h-3.5" />
          Clear filters
        </button>
      )}
    </div>
  );
}

export type { FilterState };
